import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter, first, tap } from 'rxjs/operators';
import { ConfigurationService } from './shared/services/configuration.service';
import { AppUrls } from './shared/app-urls';
import { DialogsService } from './shared/services/dialog.service';
import { AuthService } from './shared/services/auth.service';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { BusinessOperation } from './shared/models/business-operation';
import { ServerLoggerService } from './shared/services/server.service';
import { NotificationMpsService } from './shared/events/services/notification-mps.service';
import { NotificationManager } from '../assets/notification-manager';
import { CustomerEventsService } from './shared/events/services/customer-events.service';
import { SupplierEventsService } from './shared/events/services/supplier-events.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public isAuthorized: boolean = false;
  public pageHeader: string = '';
  public breadcrumbsAngularRoutes = AppUrls.angularRoutes;
  public isCustomer: boolean = false;

  constructor(
    private router: Router,
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogsService,
    private _configurationService: ConfigurationService,
    private _authService: AuthService,
    private _serverLogger: ServerLoggerService,
    private _notificationsService: NotificationMpsService,
    private _customerEventsService: CustomerEventsService,
    private _supplierEventsService: SupplierEventsService,
  ) {
    this.dialogService.setRootViewContainerRef(this.viewContainerRef);
  }

  ngOnInit() {
    this.isAuthorized = !!this._configurationService.user;
    this.isCustomer = this._configurationService.user?.isCustomer;
    this._refreshNotifications();
    this._notificationsService.onChange.pipe(
      tap(() => this._refreshNotifications()),
      untilDestroyed(this)
    ).subscribe();

    this._initEventsService();
    this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe(() => {
        window.scrollTo(0, 0);
      });

    this.router.events.subscribe((event) => {
      if (event instanceof GuardsCheckEnd) {
        if (event.shouldActivate) {
          let data = event.state.root;
          while (data.firstChild !== null) {
            data = data.firstChild;
          }
          this.pageHeader =
            data.component?.name && data.component.name !== 'CustomerRegistryComponent'
              ? data.data.title
              : '';
        }
      }
      if (event instanceof NavigationEnd) {
        this._authService.renewSecurityToken();
      }
      window.scrollTo(0, 0);
    });
  }

  private _initEventsService(): void {
    if (this._configurationService.user?.isCustomer) {
      this._customerEventsService.start(this._configurationService.serverDateTimeBehaviorSubject);
    }
    if (this._configurationService.user?.isParticipant) {
      this._supplierEventsService.start(this._configurationService.serverDateTimeBehaviorSubject);
    }
  }

  private _refreshNotifications(): void {
    this._notificationsService
      .getNotifications()
      .pipe(
        tap((notifications) => {
          const notificationManager = new NotificationManager();
          notificationManager.refreshNotifications(notifications);

          const notificationElements = document.querySelectorAll('rts-notification');
          notificationElements.forEach((notification) => {
            const interactiveElements = notification.shadowRoot.querySelectorAll<HTMLElement>(
              '.notification__content a, .notification__content button'
            );
            interactiveElements.forEach((element) => {
              element.addEventListener('click', () => this._logNotificationClick(element, notification));
            });
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private _logNotificationClick(element: HTMLElement, notification: Element): void {
    const params = {
      Operation: 'AlertButtonClick',
      Comment: `Переход по ${element instanceof HTMLAnchorElement ? element.href : element.innerText}. Slot-id: ${notification.getAttribute('slot-id')}`,
      BusinessObjectType: 'Alerts',
    } as BusinessOperation;

    this._serverLogger
      .logBusinessOperation(params, true)
      .pipe(first(), untilDestroyed(this))
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this._configurationService.user?.isCustomer) {
      this._customerEventsService.stop();
    }
    if (this._configurationService.user?.isParticipant) {
      this._supplierEventsService.stop();
    }
  }
}
